'use client';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Image from 'next/image';

const components = {
  h1: ({ children, ...props }: any) => (
    <h1
      className='mb-4 text-4xl font-bold text-[#1c2534] dark:text-white'
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: any) => {
    // Extract text content from children (handles nested elements like bold)
    const getText = (node: any): string => {
      if (typeof node === 'string') return node;
      if (Array.isArray(node)) return node.map(getText).join('');
      if (node?.props?.children) return getText(node.props.children);
      return '';
    };
    const text = getText(children);
    const id = text ? text.replace(/\s+/g, '-').toLowerCase() : undefined;
    return (
      <h2
        id={id}
        className='mb-3 mt-6 scroll-m-[100px] text-2xl font-semibold text-[#1c2534] dark:text-white sm:scroll-m-[200px] lg:text-3xl'
        {...props}
      >
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }: any) => (
    <h3
      className='mb-2 mt-4 text-xl font-medium text-[#1c2534] dark:text-white lg:text-2xl'
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }: any) => (
    <h4
      className='mb-2 mt-3 text-lg font-medium text-[#1c2534] dark:text-white'
      {...props}
    >
      {children}
    </h4>
  ),
  p: ({ children, ...props }: any) => (
    <p className='mb-4 leading-[1.7] text-[#1c2534] dark:text-white' {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }: any) => (
    <ul
      className='mb-4 ml-[20px] list-disc text-[#1c2534] dark:text-white'
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: any) => (
    <ol
      className='mb-4 ml-[18px] list-decimal text-[#1c2534] dark:text-white'
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }: any) => (
    <li className='mb-2 text-[#1c2534] dark:text-white' {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }: any) => (
    <blockquote
      className='my-4 border-l-4 border-[#00b4b9] bg-gray-50 py-2 pl-4 italic dark:bg-gray-800'
      {...props}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, children, ...props }: any) =>
    inline ? (
      <code
        className='rounded bg-gray-100 px-1 py-0.5 text-sm dark:bg-gray-700'
        {...props}
      >
        {children}
      </code>
    ) : (
      <code {...props}>{children}</code>
    ),
  pre: ({ children, ...props }: any) => (
    <pre
      className='mb-4 overflow-x-auto rounded-lg bg-gray-100 p-4 dark:bg-gray-800'
      {...props}
    >
      {children}
    </pre>
  ),
  strong: ({ children, ...props }: any) => (
    <strong className='font-semibold text-[#1c2534] dark:text-white' {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }: any) => (
    <em className='italic' {...props}>
      {children}
    </em>
  ),
  a: ({ href, children, ...props }: any) => (
    <a
      href={href}
      className='text-[#00b4b9] underline hover:text-[#008b8f]'
      target='_blank'
      rel='noopener noreferrer'
      {...props}
    >
      {children}
    </a>
  ),
  img: ({ src, alt, ...props }: any) => (
    <Image
      src={src || ''}
      alt={alt || ''}
      width={800}
      height={400}
      className='my-4 rounded-lg'
      {...props}
    />
  ),
  table: ({ children, ...props }: any) => (
    <div className='my-4 overflow-x-auto'>
      <table
        className='min-w-full border-collapse border border-gray-300 dark:border-gray-600'
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: any) => (
    <thead className='bg-gray-100 dark:bg-gray-700' {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }: any) => <tbody {...props}>{children}</tbody>,
  tr: ({ children, ...props }: any) => (
    <tr className='border-b border-gray-300 dark:border-gray-600' {...props}>
      {children}
    </tr>
  ),
  th: ({ children, ...props }: any) => (
    <th
      className='border border-gray-300 px-4 py-2 text-left font-semibold text-[#1c2534] dark:border-gray-600 dark:text-white'
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }: any) => (
    <td
      className='border border-gray-300 px-4 py-2 text-[#4a5568] dark:border-gray-600 dark:text-gray-300'
      {...props}
    >
      {children}
    </td>
  ),
  hr: (props: any) => (
    <hr className='my-6 border-gray-300 dark:border-gray-600' {...props} />
  ),
};

export function CustomMDX({
  source,
  ...rest
}: {
  source: string;
  [key: string]: any;
}) {
  if (!source) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
    >
      {source}
    </ReactMarkdown>
  );
}
